import { z } from "zod";

/**
 * Door creation + House Rules read contracts — PRD §5.1 / §5.2, FR-1 Track A.
 *
 * Nob operates no handle namespace (FR-1.1): nothing here names, checks or
 * reserves a handle. The address is derived from the caller's X identity.
 */

/** A criteria chip from the Track A screens. Angle brackets are stripped on save (FR-2.4). */
const Chip = z.string().trim().min(1).max(160);

export const DoesntChip = z.object({
  text: Chip,
  /** `hard` compiles to a block; `soft` to a strong prior the doorman may be argued out of. */
  severity: z.enum(["hard", "soft"]),
});
export type DoesntChip = z.infer<typeof DoesntChip>;

function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * `POST /api/v1/door/create`. Every field is optional — skipping both criteria
 * screens is a supported path and still yields a live door (FR-1.4).
 */
export const DoorCreateRequest = z.object({
  gets_through: z.array(Chip).max(12).optional(),
  doesnt: z.array(DoesntChip).max(12).optional(),
  persona_name: z.string().trim().min(1).max(40).optional(),
  persona_tone: z.string().trim().min(1).max(120).optional(),
  timezone: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isValidTimeZone, "unknown IANA time zone")
    .optional(),
});
export type DoorCreateRequest = z.infer<typeof DoorCreateRequest>;

export const DoorCreateResponse = z.object({
  door: z.object({
    handle: z.string(),
    status: z.literal("live"),
    public_url: z.string(),
  }),
  /** The full markdown is returned and must be rendered (PRD §5.1). */
  house_rules: z.object({ version: z.number().int().positive(), markdown: z.string() }),
});
export type DoorCreateResponse = z.infer<typeof DoorCreateResponse>;

export const RulesCurrentResponse = z.object({
  door_id: z.string(),
  version: z.number().int().positive(),
  markdown: z.string(),
});
export type RulesCurrentResponse = z.infer<typeof RulesCurrentResponse>;
